import { Add, Check, Clear, FileCopy, WhatsApp } from "@mui/icons-material";
import { Breadcrumbs, Button, Checkbox, FormControl, IconButton, InputLabel, Link, MenuItem, Select, TextField, Typography } from "@mui/material";
import { useAtom } from "jotai";
import { FC, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { modalConfirmLogout, modalConfirmSend, modalDocumentGroup, modalQrCodeWhatsApp, permissionsAtom, selectedDocumentsAtom } from "../../atoms/atom";
import AppBar from "../../components/app-bar/AppBar";
import ConfirmLogoutModal from "../../components/documents/confirm-logout/ConfirmLogoutModal";
import ConfirmSendModal from "../../components/documents/confirm-send-modal/ConfirmSendModal";
import CreateGroupModal from "../../components/documents/create-group-modal/CreateGroupModal";
import QrCodeWhatsAppModal from "../../components/documents/qr-code-whatsapp-modal/QrCodeWhatsAppModal";
import { DocumentsService } from "../../services/DocumentService";
import { EvolutionService } from "../../services/EvolutionService";
import { DocumentsGroupDTO } from "../../types/DocumentsGroupDTO";
import { useUtils } from "../../utils/utilFunctions";
import { getLoading } from "../execution-events/ExecutionEvents";
import { obtemIconeOrdem } from "../executions/Executions";
import './Documents.scss';

const Documents: FC = () => {
	const [search, setSearch] = useState('');
	const [groups, setGroups] = useState<DocumentsGroupDTO[]>([]);
	const [filteredGroups, setFilteredGroups] = useState<DocumentsGroupDTO[]>([]);
	const [loading, setLoading] = useState(true);
	const [fieldOrder, setFieldOrder] = useState<'NAME' | 'DATE'>('DATE');
	const [ordemAsc, setOrdemAsc] = useState(false);
	const [openCreate, setOpenCreate] = useAtom(modalDocumentGroup);
	const [openQrCode, setOpenQrCode] = useAtom(modalQrCodeWhatsApp);
	const [openConfirmSend, setOpenConfirmSend] = useAtom(modalConfirmSend);
	const [openConfirmLogout, setOpenConfirmLogout] = useAtom(modalConfirmLogout);
	const [, setSelectedDocuments] = useAtom(selectedDocumentsAtom);
	const [whatsAppStatusOk, setWhatsAppStatusOk] = useState(false);
	const [permissions] = useAtom(permissionsAtom);
	const [selectedGroups, setSelectedGroups] = useState<number[]>([]);
	const [filterStatus, setFilterStatus] = useState<'ALL' | 'SENT' | 'NOT_SENT'>('ALL');
	const service = new DocumentsService();
	const { verifyPermission, handleCopy, formatStringFullDate } = useUtils();
	const navigate = useNavigate();

	const sendableGroups = filteredGroups.filter(g => g.toSend && g.toSend.length > 0);
	const allSelected = sendableGroups.length > 0 && selectedGroups.length === sendableGroups.length;

	function mudaOrdem(field: 'NAME' | 'DATE') {
		setFieldOrder(field);
		setOrdemAsc(!ordemAsc);
	}

	const goToDeliveryDocuments = (id: number) => {
		navigate(`/envio-de-documentos/${id}/envios`);
	}

	const configureWhatsApp = () => {
		if (whatsAppStatusOk) {
			setOpenConfirmLogout(true);
			return;
		}
		setOpenQrCode(true);
	}

	const handleSelectGroup = (groupId: number) => {
		if (selectedGroups.includes(groupId)) {
			setSelectedGroups(selectedGroups.filter(id => id !== groupId));
		} else {
			setSelectedGroups([...selectedGroups, groupId]);
		}
	};

	const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
		if (event.target.checked) {
			setSelectedGroups(sendableGroups.map(g => g.companyId));
		} else {
			setSelectedGroups([]);
		}
	};

	const handleSendSelected = () => {
		const documentsToSend = groups.filter(g => selectedGroups.includes(g.companyId)).flatMap(g => g.toSend || []);
		setSelectedDocuments(documentsToSend);
		setOpenConfirmSend(true);
	};

	useEffect(() => {
		if (!!permissions.length) {
			verifyPermission(permissions, 'DOCUMENTS');
		}
	}, []);

	useEffect(() => {
		if (openCreate || openConfirmSend) return;
		const loadGroups = async () => {
			const response = await service.getGroups();
			if (response) {
				setGroups(response.data);
				setFilteredGroups(response.data);
			}
			setLoading(false);
		};
		loadGroups();
		setSelectedGroups([]);
	}, [openCreate, openConfirmSend]);

	useEffect(() => {
		let filtered = groups;

		if (filterStatus === 'SENT') {
			filtered = filtered.filter(g => !g.toSend || g.toSend.length === 0);
		} else if (filterStatus === 'NOT_SENT') {
			filtered = filtered.filter(g => g.toSend && g.toSend.length > 0);
		}

		if (search) {
			filtered = filtered.filter(item =>
				item.companyNameGroup.toUpperCase().includes(search.toUpperCase())
			);
		}

		setFilteredGroups(filtered);
	}, [search, groups, filterStatus]);

	useEffect(() => {
		const load = async () => {
			if (openQrCode || openConfirmLogout) return;
			const response = await new EvolutionService().getStatus();
			if (response && response?.data) {
				setWhatsAppStatusOk(response.data.instance.state === "open");
			}
		};
		load();
	}, [openQrCode, openConfirmLogout]);

	return (
		<div className="documents">
			<AppBar
				modulo="Envio de documentos"
			/>
			<div className="component">
				<div className="top">
					<div className="etapas">
						<Breadcrumbs aria-label="breadcrumb">
							<Link variant="h6" underline="hover" color="inherit" href="/envio-de-documentos">
								Envio de documentos
							</Link>
						</Breadcrumbs>
					</div>
					<div className="document-inputs">
						{selectedGroups.length > 0 &&
							<Button
								variant='contained'
								color='secondary'
								onClick={handleSendSelected}
							>
								Enviar Selecionados
							</Button>
						}
						<Button
							variant='contained'
							color={whatsAppStatusOk ? 'success' : 'error'}
							onClick={() => configureWhatsApp()}
							id='status-whatsapp'
							data-shaking={!whatsAppStatusOk && !openQrCode}
						>
							{whatsAppStatusOk
								? <>
									<Check sx={{marginRight: "1vh"}}/> WhatsApp conectado <WhatsApp sx={{marginLeft: "1vh"}}/>
								</>
								: <>
									<Clear sx={{marginRight: "1vh"}}/> Conectar WhatsApp
								</>
							}
						</Button>
						<Button
							variant="contained"
							color="info"
							onClick={() => setOpenCreate(true)}
							startIcon={<Add />}
						>
							Adicionar empresa
						</Button>
						<FormControl size="small" sx={{ width: '200px' }}>
							<InputLabel id="filter-status-label">Filtrar por status</InputLabel>
							<Select
								labelId="filter-status-label"
								id="filter-status-select"
								value={filterStatus}
								label="Filtrar por status"
								onChange={(e) => setFilterStatus(e.target.value as 'ALL' | 'SENT' | 'NOT_SENT')}
							>
								<MenuItem value={'ALL'}>Todos</MenuItem>
								<MenuItem value={'NOT_SENT'}>Envios pendentes</MenuItem>
								<MenuItem value={'SENT'}>Enviados</MenuItem>
							</Select>
						</FormControl>
						<TextField
							sx={{width: '300px'}}
							label="Buscar empresa pelo nome"
							id="group-search"
							size="small"
							value={search}
							onChange={(e) => setSearch(e.target.value)}
						/>
					</div>
				</div>
				{!loading && filteredGroups && filteredGroups.length
					? <div className="tabela">
						<div className="header-tabela">
							<p onClick={() => mudaOrdem('NAME')} id="sort">
								<Checkbox
									onChange={handleSelectAll}
									checked={allSelected}
									indeterminate={selectedGroups.length > 0 && !allSelected}
									disabled={sendableGroups.length === 0}
									onClick={e => e.stopPropagation()}
								/>
								Empresa {obtemIconeOrdem('NAME', fieldOrder, ordemAsc)}
							</p>
							<p>CNPJ matriz</p>
							<p onClick={() => mudaOrdem('DATE')} id="sort-x">Último envio {obtemIconeOrdem('DATE', fieldOrder, ordemAsc)}</p>
						</div>
						<div className="body-table">
							{filteredGroups.sort((a, b) => {
								let valueA: string | number;
								let valueB: string | number;
								switch (fieldOrder) {
									case 'DATE':
										valueA = new Date(a.lastDelivery).getTime();
										valueB = new Date(b.lastDelivery).getTime();
										break;
									case 'NAME':
										valueA = a.companyNameGroup;
										valueB = b.companyNameGroup;
										break;
								}
								if (typeof valueA === 'string' && typeof valueB === 'string') {
									return ordemAsc
										? valueA.localeCompare(valueB)
										: valueB.localeCompare(valueA);
								} else {
									return ordemAsc
										? (valueA as number) - (valueB as number)
										: (valueB as number) - (valueA as number);
								}
							}).map((group) => (
								<div id={group.companyNameGroup + ''} className="linha-empresa">
									<div style={{ display: 'flex', alignItems: 'center' }}>
										{group.toSend && group.toSend.length > 0 ?
											<Checkbox
												checked={selectedGroups.includes(group.companyId)}
												onChange={() => handleSelectGroup(group.companyId)}
											/>
											: <div style={{ width: '42px' }} />
										}
									</div>
									<p onClick={() => goToDeliveryDocuments(group.companyId)} id="sort">
										{group.companyNameGroup}
									</p>
									<p style={{cursor: "default"}}>
										{group.cnpj}
										<IconButton onClick={() => handleCopy(group.cnpj)}>
											<FileCopy />
										</IconButton>
									</p>
									<p onClick={() => goToDeliveryDocuments(group.companyId)}>
										{formatStringFullDate(group.lastDelivery)}
									</p>
								</div>
							))}
						</div>
					</div>
				: !loading ? <div className="sem-execucoes">
					<Typography variant="h5">
						Nenhuma empresa cadastrada ainda ...
					</Typography>
				</div> : <></>}
				{loading
					? getLoading()
					: <></>
				}
			</div>
			<CreateGroupModal
				create={(cnpj) => service.create(cnpj)}
				cnpjs={groups.map(group => group.cnpj)}
			/>
			<QrCodeWhatsAppModal/>
			<ConfirmLogoutModal/>
			<ConfirmSendModal/>
		</div>
	);
}

export default Documents;